import { Reference } from '@approvalmax/types';
import { DateTimeEditor, DropdownEditor, TextEditor } from '@approvalmax/ui';
import { Text, Tooltip } from '@approvalmax/ui/src/components';
import { stringHelpers } from '@approvalmax/utils';
import { useAtomValue } from 'jotai';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { useDispatch, useSelector } from 'modules/react-redux';
import moment from 'moment';
import { useOcrData } from 'pages/requestForm/hooks/useOcrData';
import {
    getCanCreateContact,
    getFieldsApprovalPermissionBySystemPurpose,
    getRequestEditMode,
    RequestEditMode,
} from 'pages/requestForm/selectors/requestSelectors';
import { getXeroContext } from 'pages/requestForm/selectors/xero';
import { FC, memo, useCallback, useMemo, useRef, useState } from 'react';
import { useMount } from 'react-use';
import { ocrIsLoadingState } from 'shared/states';

import { changeXeroSupplier, editRequestField, showXeroContactCreationPopup } from '../../../../../actions';
import { XeroContact } from '../../../../../data/xero/XeroContact';
import { useGetRecognizedOcrAttachment } from '../../../../../hooks/useGetRecognizedOcrAttachment';
import { useOcrStatus } from '../../../../../hooks/useOcrStatus';
import { getShowValidationErrors } from '../../../../../selectors/pageSelectors';
import { getDefaultDueDate } from '../../../../../utils/getDefaultDueDate';
import CustomerContactDataProvider from '../../../dataProviders/CustomerContactDataProvider';
import { XeroContactDetails } from '../../../XeroContactDetails/XeroContactDetails';
import { messages } from './XeroSalesInvoiceFieldsSection.messages';
import { ContactFieldItem, FieldItem, Root } from './XeroSalesInvoiceFieldsSection.styles';
import { XeroSalesInvoiceFieldsSectionProps } from './XeroSalesInvoiceFieldsSection.types';

const { xeroConstants, commonConstants } = constants;

const XeroSalesInvoiceFieldsSection: FC<XeroSalesInvoiceFieldsSectionProps> = memo((props) => {
    const { request } = props;
    const dispatch = useDispatch();
    const xeroContext = useSelector(getXeroContext);

    const { contact, date, dueDate, reference, brandingTheme } = request.details;
    const showValidationErrors = useSelector(getShowValidationErrors);
    const editMode = useSelector((state) => getRequestEditMode(state, request));
    const isApproverMode = editMode === RequestEditMode.Approver;
    const initialBrandingTheme = useRef(brandingTheme);
    const { defaultPaymentTerms } = useSelector(getXeroContext);
    const [paymentTerm, setPaymentTerm] = useState<domain.XeroPaymentTerm | undefined>(
        contact?.paymentTerms?.sales ? contact.paymentTerms.sales : defaultPaymentTerms?.sales
    );
    const ocrIsLoading = useAtomValue(ocrIsLoadingState);

    useOcrStatus(request);

    const ocrAttachment = useGetRecognizedOcrAttachment(request);

    const { data: ocrData } = useOcrData(request, ocrAttachment?.id);

    const showCustomerWarning = ocrData?.document?.customerIds && ocrData.document.customerIds.length > 1;
    const showDueDateWarning = ocrAttachment && moment(dueDate).isBefore(date);

    const [
        customerFieldPermission,
        dateFieldPermission,
        dueDateFieldPermission,
        referenceFieldPermission,
        brandingFieldPermission,
    ] = useSelector((state) =>
        getFieldsApprovalPermissionBySystemPurpose(state, request, [
            domain.FieldSystemPurpose.XeroCustomer,
            domain.FieldSystemPurpose.XeroDate,
            domain.FieldSystemPurpose.XeroDueDate,
            domain.FieldSystemPurpose.XeroReference,
            domain.FieldSystemPurpose.XeroBranding,
        ])
    );

    const createContact = useCallback(
        (text: string | null) => {
            dispatch(showXeroContactCreationPopup(text));

            return Promise.resolve(null);
        },
        [dispatch]
    );

    const onChangeSupplier = useCallback(
        (supplier: XeroContact | null) => {
            dispatch(changeXeroSupplier(supplier));

            if (date) {
                setPaymentTerm(supplier?.paymentTerms?.sales || defaultPaymentTerms?.sales);
            }
        },
        [date, defaultPaymentTerms?.sales, dispatch]
    );

    const onChangeDueDate = useCallback(
        (date: string | null) => {
            dispatch(editRequestField<domain.XeroInvoiceDetails>('dueDate', date));
        },
        [dispatch]
    );

    const updateDueDate = useCallback(
        (newDate: string | null, newPaymentTerm: domain.XeroPaymentTerm | undefined) => {
            if (newDate && newPaymentTerm) {
                const dueDate = getDefaultDueDate(newDate, newPaymentTerm.day, newPaymentTerm.termType);

                onChangeDueDate(dueDate);
            }
        },
        [onChangeDueDate]
    );

    const onChangeDate = useCallback(
        (date: string | null) => {
            dispatch(editRequestField<domain.XeroInvoiceDetails>('date', date));
            updateDueDate(date, paymentTerm);
        },
        [dispatch, updateDueDate, paymentTerm]
    );

    const onChangeReference = useCallback(
        (reference: string) => {
            if (commonConstants.ONLY_SPACES_REG_EXP.test(reference)) {
                return;
            }

            dispatch(
                editRequestField<domain.XeroInvoiceDetails>(
                    'reference',
                    stringHelpers.escapeSpecificExpressions(reference, xeroConstants.XERO_NOT_ALLOWED_SYMBOLS)
                )
            );
        },
        [dispatch]
    );

    const onChangeBrandingTheme = useCallback(
        (brandingTheme: Reference | null) => {
            dispatch(editRequestField<domain.XeroInvoiceDetails>('brandingTheme', brandingTheme));
        },
        [dispatch]
    );

    const canCreateContact = useSelector((state) => getCanCreateContact(state, request));

    const createItemDisplayTextFn = useCallback(() => messages.addCustomerButton, []);

    const isReferenceInvalid =
        showValidationErrors &&
        ((reference?.length ?? 0) > xeroConstants.INVOICE_REFERENCE_MAX_LENGTH ||
            (referenceFieldPermission.required && !reference));

    const brandingThemeOptions = useMemo(
        () =>
            isApproverMode && initialBrandingTheme.current
                ? [initialBrandingTheme.current, ...xeroContext.brandingThemes]
                : xeroContext.brandingThemes,
        [isApproverMode, xeroContext.brandingThemes]
    );

    useMount(() => {
        updateDueDate(date, paymentTerm);
    });

    return (
        <Root>
            <ContactFieldItem required title={messages.customerFieldTitle}>
                <Tooltip
                    disabled={!showCustomerWarning}
                    placement='top'
                    width='max-content'
                    activator={
                        <CustomerContactDataProvider>
                            <DropdownEditor
                                invalid={showValidationErrors && !contact}
                                value={contact}
                                warning={showCustomerWarning}
                                singleLinePerItem={false}
                                onChange={onChangeSupplier}
                                placeholder={messages.customerFieldPlaceholder}
                                canCreateItemFn={() => canCreateContact}
                                createItemDisplayTextFn={createItemDisplayTextFn}
                                onCreateItem={createContact}
                                disabled={customerFieldPermission.disabled || ocrIsLoading}
                            />
                        </CustomerContactDataProvider>
                    }
                >
                    <Text spacing='8' font='body' fontSize='small'>
                        {messages.multipleCustomersWarning}
                    </Text>
                </Tooltip>

                <XeroContactDetails contact={contact} />
            </ContactFieldItem>

            <FieldItem required title={messages.dateFieldTitle}>
                <DateTimeEditor
                    invalid={showValidationErrors && !date}
                    value={date}
                    onChange={onChangeDate}
                    placeholder={messages.dateFieldPlaceholder}
                    disabled={dateFieldPermission.disabled || ocrIsLoading}
                />
            </FieldItem>

            <FieldItem required title={messages.dueDateFieldTitle}>
                <Tooltip
                    disabled={!showDueDateWarning}
                    placement='top'
                    width='max-content'
                    activator={
                        <DateTimeEditor
                            warning={showDueDateWarning}
                            value={dueDate}
                            invalid={showValidationErrors && !dueDate}
                            onChange={onChangeDueDate}
                            placeholder={messages.dueDateFieldPlaceholder}
                            disabled={dueDateFieldPermission.disabled || ocrIsLoading}
                        />
                    }
                >
                    <Text spacing='8' font='body' fontSize='small'>
                        {messages.earlyDueDateWarning}
                    </Text>
                </Tooltip>
            </FieldItem>

            <FieldItem required={referenceFieldPermission.required} title={messages.referenceFieldTitle}>
                <TextEditor
                    invalid={isReferenceInvalid}
                    value={reference ?? ''}
                    maxLength={xeroConstants.INVOICE_REFERENCE_MAX_LENGTH}
                    onChange={onChangeReference}
                    placeholder={messages.referenceFieldPlaceholder}
                    disabled={referenceFieldPermission.disabled || ocrIsLoading}
                />
            </FieldItem>

            <FieldItem title={messages.themeFieldTitle}>
                <PlainDataProvider items={brandingThemeOptions}>
                    <DropdownEditor
                        invalid={showValidationErrors && !brandingTheme}
                        value={brandingTheme}
                        onChange={onChangeBrandingTheme}
                        placeholder={messages.themeFieldPlaceholder}
                        buttonComponent={DropdownEditor.NoFilterButton}
                        disabled={brandingFieldPermission.disabled || ocrIsLoading}
                    />
                </PlainDataProvider>
            </FieldItem>
        </Root>
    );
});

export default XeroSalesInvoiceFieldsSection;
